import { gql } from '@apollo/client';

import { PaginationArg } from '@/types';

export const ImageQuery = (image: string) => {
  return `
        ${image} { 
            alternativeText
            formats
            url
            width
            height,
            mime
        }
    `;
};

type AttributeOptionTypeValues = FieldType | AttributeOptions;

export interface AttributeOptions {
  [key: string]: AttributeOptionTypeValues;
}

export enum FieldType {
  //eslint-disable-next-line no-unused-vars
  Text = 'Text',
  //eslint-disable-next-line no-unused-vars
  Image = 'Image',
  //eslint-disable-next-line no-unused-vars
  Boolean = 'Boolean',
  //eslint-disable-next-line no-unused-vars
  Number = 'Number',
  //eslint-disable-next-line no-unused-vars
  JSON = 'JSON',
  //eslint-disable-next-line no-unused-vars
  Blocks = 'Blocks',
}

export const SeoType = {
  metaTitle: FieldType.Text,
  metaDescription: FieldType.Text,
  metaImage: FieldType.Image,
  openGraph: {
    og_title: FieldType.Text,
    og_description: FieldType.Text,
    og_image: FieldType.Image,
    og_url: FieldType.Text,
    og_type: FieldType.Text,
  },
  keywords: FieldType.Text,
  metaRobots: FieldType.Text,
  metaViewport: FieldType.Text,
  canonicalURL: FieldType.Text,
  structuredData: FieldType.Text,
};

export function getAttributeString(attributes: AttributeOptions, indent = 0): string {
  const indentation = '  '.repeat(indent);
  let attrString = '';

  for (const key in attributes) {
    const value = attributes[key];

    // Dynamic zone / fragment
    if (key.startsWith('... on ') && value && typeof value === 'object') {
      attrString += `${indentation}${key} {\n${getAttributeString(
        value as AttributeOptions,
        indent + 1
      )}${indentation}}\n`;
      continue;
    }

    // FieldType scalars
    if (value === FieldType.Text || value === FieldType.Boolean || value === FieldType.Number) {
      attrString += `${indentation}${key}\n`;
      continue;
    } else if (value === FieldType.JSON) {
      attrString += `${indentation}${key}\n`;
      continue;
    }

    // Images
    if (value === FieldType.Image) {
      attrString += `${indentation}${ImageQuery(key)}\n`;
      continue;
    }

    // Nested objects (components, relations)
    if (typeof value === 'object' && value !== null) {
      const argsString = value.__args
        ? `(${JSON.stringify(value.__args).replace(/"([^"]+)":/g, '$1:')})`
        : '';
      attrString += `${indentation}${key}${argsString} {\n${getAttributeString(
        value as AttributeOptions,
        indent + 1
      )}${indentation}}\n`;
      continue;
    }

    attrString += `${indentation}${key}\n`;
  }

  return attrString;
}

export function strapiGql(
  collectionName: string,
  attributes: AttributeOptions,
  draftModeEnabled: boolean,
  slug?: string
) {
  const collectionQuery = `
      query CollectionQuery {
        ${collectionName}(
          status: ${draftModeEnabled ? 'DRAFT' : 'PUBLISHED'}
          ${
            slug
              ? `
            filters: { 
                Slug: { 
                    eq: "${slug}"
                }
            }`
              : ''
          }
        ) {
          documentId
          ${getAttributeString(attributes)}
        }
      }
    `;

  return gql`
    ${collectionQuery}
  `;
}

function paginationArgString(pagination?: PaginationArg): string {
  if (!pagination) return '';
  const fields = Object.entries(pagination)
    .filter(([, value]) => value !== undefined)
    .map(([key, value]) => `${key}: ${value}`);
  return fields.length > 0 ? `pagination: { ${fields.join(', ')} }` : '';
}

export function strapiGqlFull(
  collectionName: string,
  attributes: AttributeOptions,
  draftModeEnabled: boolean,
  filters: string,
  sort: string[] | undefined = undefined,
  pagination?: PaginationArg
) {
  const collectionQuery = `
      query CollectionQuery {
        ${collectionName}(
          status: ${draftModeEnabled ? 'DRAFT' : 'PUBLISHED'}
          ${filters ? `filters: ${filters}` : ''}
          ${paginationArgString(pagination)}
          ${sort ? `sort: ${JSON.stringify(sort)}` : ''}
        ) {
          documentId
          ${getAttributeString(attributes)}
        }
      }
    `;

  return gql`
    ${collectionQuery}
  `;
}
